import React from 'react';
import { detectPWA } from '../utils/pwaDetection';

interface PWAInstallPromptProps {
  onDismiss?: () => void;
  onInstall?: () => void;
}

export const PWAInstallPrompt: React.FC<PWAInstallPromptProps> = ({ 
  onDismiss, 
  onInstall 
}) => {
  const pwaStatus = detectPWA();
  const platform = pwaStatus.platform;

  const getInstallInstructions = () => {
    switch (platform) {
      case 'ios':
        return {
          title: 'Install XOXO Kiosk App',
          steps: [
            'Tap the share button at the bottom of the screen',
            'Scroll down and tap "Add to Home Screen"',
            'Tap "Add" in the top right corner',
            'Open the app from your home screen'
          ],
          icon: '📱'
        };
      case 'android':
        return {
          title: 'Install XOXO Kiosk App',
          steps: [
            'Tap the menu button (three dots) in the top right',
            'Select "Add to Home screen"',
            'Tap "Add" to confirm',
            'Open the app from your home screen'
          ],
          icon: '📱'
        };
      default:
        return {
          title: 'Install XOXO Kiosk App',
          steps: [
            'Look for the install button in your browser address bar',
            'Click "Install" to add the app',
            'Open the app from your desktop or app menu'
          ],
          icon: '💻'
        };
    }
  };

  const instructions = getInstallInstructions();

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'center',
      zIndex: 9999,
      padding: '20px',
      overflowY: 'auto',
      WebkitOverflowScrolling: 'touch'
    }}>
      <div style={{
        backgroundColor: 'var(--bg-primary, #1a1a1a)',
        border: '1px solid var(--border-color, #333)',
        borderRadius: '12px',
        padding: '32px',
        maxWidth: '400px',
        width: '100%',
        maxHeight: '90vh',
        margin: 'auto 0',
        textAlign: 'center',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.4), 0 10px 10px -5px rgba(0, 0, 0, 0.2)',
        color: 'var(--text-primary, #ffffff)',
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch'
      }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>
          {instructions.icon}
        </div>
        
        <h2 style={{
          margin: '0 0 16px 0',
          fontSize: '24px',
          fontWeight: 'bold',
          color: 'var(--text-primary, #ffffff)'
        }}>
          {instructions.title}
        </h2>
        
        <p className="text-gray-300 mb-4">
          Install this app on your device for the best experience and easy access.
        </p>
        <div className="bg-purple-900/20 border-l-4 border-purple-500 p-4 mb-6 rounded">
          <div className="flex">
            <div className="shrink-0">
              <svg className="h-5 w-5 text-purple-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-purple-300">Quick tip!</h3>
              <div className="mt-2 text-sm text-gray-300">
                <p>
                  Want to easily retrieve your order later? Install the app to save your preferences. 
                  Or feel free to continue testing without installing - totally up to you!
                </p>
              </div>
            </div>
          </div>
        </div>

        <div style={{
          textAlign: 'left',
          marginBottom: '24px',
          padding: '16px',
          backgroundColor: 'var(--bg-secondary, #2a2a2a)',
          border: '1px solid var(--border-color, #404040)',
          borderRadius: '8px'
        }}>
          <h3 style={{
            margin: '0 0 12px 0',
            fontSize: '14px',
            fontWeight: '600',
            color: 'var(--text-primary, #ffffff)'
          }}>
            {instructions.subtitle}
          </h3>
          <ol style={{
            margin: 0,
            paddingLeft: '20px',
            fontSize: '14px',
            color: 'var(--text-secondary, #a1a1aa)',
            lineHeight: '1.5'
          }}>
            {instructions.steps.map((step, index) => (
              <li key={index} style={{ marginBottom: '4px' }}>{step}</li>
            ))}
          </ol>
        </div>

        <div style={{
          display: 'flex',
          gap: '12px',
          justifyContent: 'center'
        }}>
          <button
            onClick={onInstall}
            style={{
              padding: '12px 24px',
              backgroundColor: 'var(--cocktail-pink, #ff6ea8)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s',
              boxShadow: '0 2px 4px rgba(255, 110, 168, 0.2)'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = '#ff4d94';
              e.currentTarget.style.transform = 'translateY(-1px)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--cocktail-pink, #ff6ea8)';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            Install App
          </button>
          <button
            onClick={onDismiss}
            style={{
              padding: '12px 24px',
              backgroundColor: 'transparent',
              color: 'var(--text-secondary, #a1a1aa)',
              border: '1px solid var(--border-color, #404040)',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--bg-secondary, #2a2a2a)';
              e.currentTarget.style.color = 'var(--text-primary, #ffffff)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = 'var(--text-secondary, #a1a1aa)';
            }}
          >
            Maybe Later
          </button>
        </div>

        <p style={{
          marginTop: '16px',
          fontSize: '12px',
          color: 'var(--text-muted, #71717a)',
          lineHeight: '1.4'
        }}>
          💡 Without installation, your settings and preferences may not be saved
        </p>
      </div>
    </div>
  );
};
